import { faRight } from '@fortawesome/pro-solid-svg-icons/faRight'
import { useInfiniteHits } from '@repo/typesense/client'
import { subDays, intervalToDuration, subHours } from 'date-fns'
import { useRouter } from 'next/router'
import { useMemo } from 'react'

import { Loader } from '../../../shared/components/loader'
import { useStoryblokTranslationsContext } from '../../../storyblok/context/translations-provider'
import { useStoryblokCercleGeneralSettings } from '../../../storyblok/hooks/useStoryblokCercleGeneralSettings'
import { useTranslations } from '../../../storyblok/hooks/useTranslations'

import {
  StBackgroundContainer,
  StCardContainer,
  StImage,
  StArticleTitle,
  StExtraInfoContainer,
  StLinkContainer,
  StLinkCta,
  StIcon,
  StContentContainer,
  StOverviewContainer,
  StTimestamp,
} from './Hits.styled'

import type { ArticleSearchItem } from '@repo/typesense/schemas'
import type { StoryblokImage } from 'types'

function Hit({ hit }: { hit: ArticleSearchItem }) {
  const { translations } = useStoryblokTranslationsContext()
  const { t } = useTranslations(translations)
  const { locale } = useRouter()

  let timeStampText = ''

  const now = new Date()

  const publishDate = hit.publishDate ? new Date(hit.publishDate) : new Date()

  const interval = intervalToDuration({ start: publishDate, end: now })

  // when start equals now, the object is empty
  const duration =
    Object.keys(interval).length > 0
      ? interval
      : {
          minutes: 0,
          hours: 0,
        }

  if (!interval.minutes && !interval.hours && interval.seconds) {
    timeStampText = `0 ${t('minutes')} ${t('ago')}`
  } else if (subHours(now, 1).getTime() < publishDate.getTime()) {
    timeStampText = `${duration.minutes} ${
      duration.minutes === 1 ? t('minute') : t('minutes')
    } ${t('ago')}`
  } else if (subDays(now, 1).getTime() < publishDate.getTime()) {
    timeStampText = `${duration.hours} ${
      duration.hours === 1 ? t('hour') : t('hours')
    } ${t('ago')}`
  } else {
    timeStampText = publishDate.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  return (
    <StCardContainer href={`/${hit.slug}`} title={hit.title}>
      {hit.thumbnail?.filename && (
        <StImage
          image={hit.thumbnail as StoryblokImage}
          width={100}
          height={100}
        />
      )}

      <StBackgroundContainer>
        <StContentContainer>
          <StArticleTitle>{hit.title}</StArticleTitle>
          <StExtraInfoContainer>
            <StLinkContainer>
              <StLinkCta>
                {t('read_article_cta')} <StIcon icon={faRight} />
              </StLinkCta>
            </StLinkContainer>
            <StTimestamp>{timeStampText}</StTimestamp>
          </StExtraInfoContainer>
        </StContentContainer>
      </StBackgroundContainer>
    </StCardContainer>
  )
}

export function Hits() {
  const { items } = useInfiniteHits<ArticleSearchItem>()
  const { isLoading } = useStoryblokCercleGeneralSettings()

  const uniqueItems = useMemo(() => {
    return items.filter(
      (item, index, self) =>
        self.findIndex((i) => i.uuid === item.uuid) === index
    )
  }, [items])

  if (isLoading) {
    return (
      <StCardContainer>
        <Loader />
      </StCardContainer>
    )
  }

  return items.length === 0 ? (
    <StOverviewContainer>
      <p>No results found</p>
    </StOverviewContainer>
  ) : (
    <StOverviewContainer>
      {uniqueItems.map((item) => (
        <Hit key={item.objectID} hit={item} />
      ))}
    </StOverviewContainer>
  )
}
